import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, ArrowLeft, X } from 'lucide-react';
import { newsEvents } from '../mock';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export const NewsDetail = () => {
  const { id } = useParams();
  const [lightboxImage, setLightboxImage] = useState(null);
  
  const newsItem = newsEvents.find(news => news.id === parseInt(id));

  if (!newsItem) {
    return <Navigate to="/news-events" replace />;
  }

  const hasMultipleImages = newsItem.images && newsItem.images.length > 1;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <section className="relative h-96 overflow-hidden">
        <img
          src={newsItem.thumbnail}
          alt={newsItem.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-block bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {newsItem.category}
            </div>
            <h1 className="text-3xl md:text-4xl md:text-5xl font-bold text-white mb-4">{newsItem.title}</h1>
            <div className="flex items-center space-x-2 text-white/90">
              <Calendar size={18} />
              <span className="text-lg">
                {new Date(newsItem.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/news-events">
              <Button variant="outline" className="mb-8 border-purple-600 text-purple-600 hover:bg-purple-50">
                <ArrowLeft className="mr-2" size={18} /> Back to News & Events
              </Button>
            </Link>

            <div className="prose prose-lg max-w-none">
              {newsItem.fullDescription.split('|').map((paragraph, index) => (
                <p key={index} className="text-xl text-gray-800 leading-relaxed mb-8">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            {/* Image Gallery */}
            {hasMultipleImages && (
              <div className="mt-12">
                <h2 className="text-3xl font-bold text-purple-900 mb-6">Event Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {newsItem.images.map((image, index) => (
                    <Card 
                      key={index}
                      className="group overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
                      onClick={() => setLightboxImage(image)}
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={image}
                          alt={`${newsItem.title} - ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Related Actions */}
            <div className="mt-16 p-8 bg-gradient-to-br from-purple-50 to-white rounded-2xl">
              <h3 className="text-2xl font-bold text-purple-900 mb-4">Interested in Our Activities?</h3>
              <p className="text-gray-700 mb-6">
                Learn more about Jagratha SHG and how you can get involved in our community initiatives.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/about">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                    About Us
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-purple-400 transition-colors duration-200"
            onClick={() => setLightboxImage(null)}
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>
          <img
            src={lightboxImage}
            alt="Gallery"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
